import { AnimatePresence, motion } from "framer-motion";
import { Star } from "lucide-react";
import YoutubeIconSVG from "@/assets/svg/youtube-icon.svg";
import MicrosoftIconSVG from "@/assets/svg/microsoft-icon.svg";
import SnapchatIconSVG from "@/assets/svg/snapchat-icon.svg";
import CryptoIconSVG from "@/assets/svg/creator-shield/crypto.svg";
import BadgeCheckSVG from "@/assets/svg/badge-check.svg";
import { useEffect, useState } from "react";

const badges = [
  { name: "Official YouTube Certified", icon: YoutubeIconSVG, link: "https://servicesdirectory.withyoutube.com/directory/enfinity-talent-agency/" },
  { name: "Microsoft Certified Partner", icon: MicrosoftIconSVG, link: "#" },
  { name: "Official Snap Partner", icon: SnapchatIconSVG, link: "#" },
  { name: "Crypto Payout Agency", icon: CryptoIconSVG, link: "#" },
];

export default function Header() {
  const [showRest, setShowRest] = useState(false);
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setShowRest(true), 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBadgeIndex((prev) => (prev + 1) % badges.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const current = badges[currentBadgeIndex];

  // Staggered animation variants for smooth reveal
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.4,
      },
    },
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const textVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.5,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  // Letter variants for stagger animation
  const letterVariants = {
    hidden: { 
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  // Container variants for staggering letters
  const headingSpanContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.02,
        delayChildren: 0.05,
      },
    },
  };

  return (
    <div className="relative py-20 xxs:py-28 lg:py-28 4xl:py-40 px-4 xxs:px-8 xs:px-10 sm:px-10 md:px-10 lg:px-16 xl:px-16 4xl:px-16 overflow-hidden flex item-center justify-center">
{/* Background Image */}
<motion.div
        className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat z-[5] opacity-80"
        style={{
          backgroundImage: `url('https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/case-study-bg.png')`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ opacity: 0.8, scale: 1 }}
        transition={{ duration: 3.5, ease: [0.22, 1, 0.36, 1] }}
      />
      {/* Background Video */}
      {/* <motion.video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-50 z-0"
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 0.5, scale: 1 }}
        transition={{ duration: 4, ease: [0.22, 1, 0.36, 1] }}
      >
        <source
          src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/WEBPAGE-nocard-1080p.mp4"
          type="video/mp4"
        />
      </motion.video> */}

      {/* Dark Overlay */}
      <motion.div 
        className="absolute inset-0 w-full h-full bg-black/40 z-[2]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2.5, ease: [0.22, 1, 0.36, 1] }}
      ></motion.div>
      
      <motion.div 
        className="mx-auto flex flex-col items-center gap-8 relative z-10 h-full justify-center"
        variants={containerVariants}
        initial="hidden"
        animate={showRest ? "visible" : "hidden"}
      >
        {/* Rotating Badges */}
        <motion.div
          variants={itemVariants}
          className="flex justify-center items-center z-20 w-full"
        >
          <a
            href={current.link || "#"}
            target={current.link && current.link !== "#" ? "_blank" : undefined}
            rel={current.link && current.link !== "#" ? "noopener noreferrer" : undefined}
          >
            <div className="relative h-14 w-fit overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.8, ease: "easeInOut" }}
                  className={`absolute flex items-center gap-2 rounded-full ${current.name === "Crypto Payout Agency" || current.name === "Microsoft Certified Partner" ? "py-2" : "py-1"} px-4 border border-white/20 bg-white/5 backdrop-blur-md text-white shimmer-badge`}
                >
                  <img
                    src={current.icon}
                    alt={current.name}
                    className={
                      current.name === "Crypto Payout Agency" || current.name === "Microsoft Certified Partner"
                        ? "h-6 w-6"
                        : current.name === "Official Snap Partner"
                        ? "h-8 w-6"
                        : "h-8 w-8"
                    }
                  />
                  <span className="xxs:text-xs xs:text-sm text-base">{current.name}</span>
                  <img src={BadgeCheckSVG} className="h-5 w-5" alt="Badge" />
                </motion.div>
              </AnimatePresence>
            </div>
          </a>
        </motion.div>

        {/* Trusted by Section */}
        {/* <div className="flex flex-col lg:flex-row items-center justify-center gap-2 lg:gap-[10px] rounded-[100px] h-auto lg:h-[65px]">
          <div className="flex items-center gap-[10px]">
            {[...Array(5)].map((_, index) => (
              <Star
                key={index}
                className="w-6 h-6 text-yellow-400 fill-yellow-400"
                strokeWidth={1}
              />
            ))}
          </div>
          <p className="text-lg font-medium text-white text-center lg:text-left">
            <span className="lg:hidden xxs:text-sm xs:text-base sm:text-base md:text-lg lg:text-lg enfinity-subtext-font">Trusted by 4,500+ creators</span>
            <span className="hidden lg:inline enfinity-subtext-font">Trusted by 4,500+ creators monetizing and protecting their content worldwide</span>
          </p>
        </div> */}

        {/* Main Heading */}
        <motion.h1 
          className="text-5xl xxs:text-[55px] lg:text-[80px] 4xl:text-[90px] font-normal text-white text-center leading-[106px] xxs:leading-tight xs:leading-tight sm:leading-tight md:leading-tight lg:leading-[106px] enfinity-header-font"
        >
          <motion.span
            className="block"
            variants={headingSpanContainerVariants}
            initial="hidden"
            animate={showRest ? "visible" : "hidden"}
          >
            {"Trusted by".split(" ").map((word, wordIndex) => (
              <span key={`first-word-${wordIndex}`} style={{ display: "inline-block", whiteSpace: "nowrap", marginRight: "0.25em" }} className="text-white">
                {word.split("").map((char, charIndex) => (
                  <motion.span
                    key={`first-${wordIndex}-${charIndex}`}
                    variants={letterVariants}
                    style={{ display: "inline-block" }}
                  >
                    {char}
                  </motion.span>
                ))}
              </span>
            ))}
            <motion.span 
              className="relative inline-block"
              style={{ marginRight: "0.25em" }}
              variants={textVariants}
              initial="hidden"
              animate={showRest ? "visible" : "hidden"}
            >
              {/* Stroke layer with vertical gradient mask - behind fill */}
              <span
                className="absolute inset-0 text-white z-0"
                style={{
                  WebkitTextStroke: "2px white",
                  WebkitTextFillColor: "transparent",
                  maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                  WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                  pointerEvents: "none",
                }}
              >
                agencies and
              </span>
              {/* Fill layer with gradient - on top */}
              <span
                className="relative z-10 bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, #3b82f6, #8b5cf6)",
                }}
              >
                agencies and
              </span>
            </motion.span>
            <br className="hidden 4xl:block" />
            <motion.span 
              className="relative inline-block"
              style={{ marginRight: "0.25em" }}
              variants={textVariants}
              initial="hidden"
              animate={showRest ? "visible" : "hidden"}
            >
              {/* Stroke layer with vertical gradient mask - behind fill */}
              <span
                className="absolute inset-0 text-white z-0"
                style={{
                  WebkitTextStroke: "2px white",
                  WebkitTextFillColor: "transparent",
                  maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                  WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                  pointerEvents: "none",
                }}
              >
                creators
              </span>
              {/* Fill layer with gradient - on top */}
              <span
                className="relative z-10 bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, #14b8a6, #3b82f6)",
                }}
              >
                creators
              </span>
            </motion.span>
            {"worldwide".split(" ").map((word, wordIndex) => (
              <span key={`last-word-${wordIndex}`} style={{ display: "inline-block", whiteSpace: "nowrap", marginRight: "0.25em" }} className="text-white">
                {word.split("").map((char, charIndex) => (
                  <motion.span
                    key={`last-${wordIndex}-${charIndex}`}
                    variants={letterVariants}
                    style={{ display: "inline-block" }}
                  >
                    {char}
                  </motion.span>
                ))}
              </span>
            ))}
          </motion.span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p 
          className="text-2xl xxs:text-[1rem] lg:text-base 4xl:text-base font-normal text-white text-center max-w-[978px] leading-normal enfinity-subtext-font"
          variants={itemVariants}
        >
          Explore how teams use Distribution, Protection, and Payments to expand reach through Microsoft MSN, recover lost value from unauthorized use, and improve payout speed and efficiency.
        </motion.p>
      </motion.div>
    </div>
  );
}
